import classNames from 'classnames';
import type { userAgent } from 'next/server';
import { ComponentProps, Dispatch, SetStateAction } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import type PurchaseTier from '@alltrails/core/types/PurchaseTier';
import SectionDivider from '@alltrails/denali/components/SectionDivider';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import type CountryCode from '@alltrails/types/CountryCode';
import type Coupon from '@alltrails/types/Coupon';
import type CurrencyCode from '@alltrails/types/CurrencyCode';
import type { Plan } from '@alltrails/types/Plan';
import type ProResponse from '../../../types/ProResponse';
import type { CardElementChangeEvent } from '../../../types/ReactRecurly';
import { CARD_VALIDATION_NAMES, TokenValidationErrors } from '../../../types/RecurlyTokenization';
import TokenizedCreditCardForm from '../../TokenizedCreditCardForm';
import type ApplePayButtonWithSDK from '../ApplePayButtonWithSDK';
import ErrorText from '../ErrorText';
import GooglePayButton from '../GooglePayButton';
import LoadingOverlay from '../LoadingOverlay';
import PayPalButton from '../PayPalButton';
import PaymentOptions, { PaymentOptionValues } from './PaymentOptions';
import styles from './RadioPaymentForm.module.scss';

type Props = {
  billingCountry: CountryCode;
  coupon?: Coupon;
  currencyCode: CurrencyCode;
  errors?: ProResponse['errors'];
  isGooglePayAvailable: boolean;
  isIosNativeApp: boolean;
  isUpgrade?: boolean;
  onApplePayToken: ComponentProps<typeof ApplePayButtonWithSDK>['onApplePayToken'];
  onBillingCountryChange: (countryCode: CountryCode, arg1: any) => void;
  onGooglePayToken: ComponentProps<typeof GooglePayButton>['onGooglePayToken'];
  onPayPalToken: ComponentProps<typeof PayPalButton>['onPayPalToken'];
  plan: Plan;
  selectedPaymentType?: PaymentOptionValues;
  setClientValidationErrors: (inputName: CARD_VALIDATION_NAMES, value: string | CardElementChangeEvent) => void;
  setErrors: Dispatch<SetStateAction<ProResponse['errors'] | undefined>>;
  setSelectedPaymentType: Dispatch<SetStateAction<PaymentOptionValues | undefined>>;
  submitting: boolean;
  validationErrors: TokenValidationErrors;
  purchaseTier: PurchaseTier;
};

export default function RadioPaymentForm({
  billingCountry,
  currencyCode,
  errors,
  isGooglePayAvailable,
  isIosNativeApp,
  isUpgrade,
  onBillingCountryChange,
  purchaseTier,
  selectedPaymentType,
  setClientValidationErrors,
  setErrors,
  setSelectedPaymentType,
  submitting,
  validationErrors
}: Props) {
  const browserName = useSelector((state: { browser: ReturnType<typeof userAgent>['browser'] }) => state.browser.name);
  const isMobile = useIsMobileSizedScreen();

  const handleChange = (newPaymentType: PaymentOptionValues) => {
    setSelectedPaymentType(newPaymentType);
  };

  if (isIosNativeApp && selectedPaymentType === PaymentOptionValues.Apple) {
    return null;
  }

  return (
    <>
      {!isIosNativeApp && (
        <div className={classNames(styles.container, { [styles.iosWebCheckoutOptimizationEnabled]: isIosNativeApp })}>
          <SectionDivider classes={styles.topRadioFormDivider} />
          <PaymentOptions
            isMobile={isMobile}
            currencyCode={currencyCode}
            browserName={browserName}
            selectedPaymentType={selectedPaymentType}
            handleChange={handleChange}
            isGooglePayAvailable={isGooglePayAvailable}
            isIosNativeApp={isIosNativeApp}
            shouldHideRadioForm={isIosNativeApp}
          />
        </div>
      )}
      {/* Hide the card details divider if the iOS web checkout optimization is enabled */}
      {!isIosNativeApp && (
        <div>
          <SectionDivider>
            {selectedPaymentType === PaymentOptionValues.CreditCard ? <FormattedMessage defaultMessage="Enter your card details" /> : null}
          </SectionDivider>
        </div>
      )}
      {selectedPaymentType === PaymentOptionValues.CreditCard && (
        <TokenizedCreditCardForm
          validationErrors={validationErrors}
          handleInputChange={(input, value: string | unknown) => {
            if (errors) {
              setErrors(undefined);
            }

            setClientValidationErrors(input, value as string | CardElementChangeEvent);
          }}
          billingCountry={{
            onChange: onBillingCountryChange,
            value: billingCountry
          }}
          isUpgrade={isUpgrade}
          purchaseTier={purchaseTier}
        />
      )}
      {errors && <ErrorText errors={errors} />}
      {submitting && <LoadingOverlay />}
    </>
  );
}
